const asArray = (value) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const text = (value) => String(value ?? '').trim();

const unique = (values) => {
  const seen = new Set();
  return values.filter((value) => {
    const key = text(value);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const normalizeRoleValue = (role) => {
  const raw =
    role?.role_name ||
    role?.name ||
    role?.slug ||
    role?.title ||
    role?.role ||
    role?.value ||
    role ||
    '';

  const normalized = text(raw)
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

  return normalized === 'admin' ? 'super_admin' : normalized;
};

export const roleLabel = (role) =>
  text(role)
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

export const getRoleFromUser = (user = {}) =>
  normalizeRoleValue(
    user?.role?.role_name ||
      user?.role?.name ||
      user?.role?.slug ||
      user?.role ||
      user?.role_name ||
      user?.user_role?.role_name ||
      user?.user_role?.name ||
      localStorage.getItem('role') ||
      sessionStorage.getItem('role') ||
      ''
  );

export const getStoredUser = () => {
  try {
    const hasLocalToken = !!localStorage.getItem('token');
    const hasSessionToken = !!sessionStorage.getItem('token');
    
    let raw = null;
    if (hasSessionToken && !hasLocalToken) {
      raw = sessionStorage.getItem('user');
    } else {
      raw = localStorage.getItem('user') || sessionStorage.getItem('user');
    }

    const userObj = applyUserAccessOverride(JSON.parse(raw || '{}') || {});
    
    const derivedName = userObj.full_name || userObj.name || [userObj.first_name, userObj.last_name].filter(Boolean).join(" ") || userObj.username || "";
    if (derivedName) {
      userObj.full_name = derivedName;
    }

    return userObj;
  } catch (err) {
    return {};
  }
};

export const applyUserAccessOverride = (user = {}) => {
  const hasLocalToken = !!localStorage.getItem('token');
  const hasSessionToken = !!sessionStorage.getItem('token');
  const storage = (hasSessionToken && !hasLocalToken) ? sessionStorage : localStorage;

  const userId = user?.id || user?.user_id || storage.getItem('userId') || '';
  const email = user?.email || storage.getItem('email') || '';

  try {
    const overrides = JSON.parse(localStorage.getItem('admin.userManagement.overrides') || '{}');
    const override = overrides[userId] || overrides[email];
    if (!override?._clinicAssignmentOverride) return user;

    const clinicId = override.clinic_id || override.assigned_clinic_id || override.clinic?.id || override.clinic?.clinic_id || '';
    const clinicName = override.clinic_name || override.clinic?.name || override.clinic?.clinic_name || '';

    return {
      ...user,
      ...override,
      id: user?.id || override.id,
      user_id: user?.user_id || override.user_id,
      role: user?.role || override.role,
      role_name: user?.role_name || override.role_name,
      clinic_id: clinicId,
      clinic_ids: clinicId ? [String(clinicId)] : [],
      clinic_name: clinicName,
      assigned_clinic_id: clinicId || null,
      clinic: clinicId
        ? {
            ...(user?.clinic || {}),
            ...(override?.clinic || {}),
            id: clinicId,
            clinic_id: clinicId,
            name: clinicName,
            clinic_name: clinicName
          }
        : user?.clinic
    };
  } catch {
    return user;
  }
};

const collectClinicIds = (source = {}) => {
  const ids = [
    source?.clinic_id,
    source?.clinicId,
    source?.primary_clinic_id,
    source?.assigned_clinic_id,
    source?.assignedClinicId,
    source?.clinic?.id,
    source?.clinic?.clinic_id,
    source?.assigned_clinic?.id,
    source?.assigned_clinic?.clinic_id,
    source?.admin?.clinic_id,
    source?.owner?.clinic_id,
    source?.clinic_admin?.clinic_id,
    source?.clinic_admin?.clinic?.id,
    source?.doctor?.clinic_id,
    source?.doctor_info?.clinic_id,
    source?.patient?.clinic_id,
    source?.patient_info?.clinic_id
  ];

  if (source?.clinic_type_id !== undefined || source?.pincode !== undefined || source?.type_name !== undefined) {
    ids.push(source.id || source.clinic_id);
  }

  [
    source?.clinic_ids,
    source?.clinicIds,
    source?.clinics,
    source?.assigned_clinics,
    source?.admin_clinics,
    source?.owned_clinics,
    source?.doctor_clinics,
    source?.doctor?.clinics,
    source?.doctor_info?.clinics,
    source?.patient?.clinics,
    source?.patient_info?.clinics
  ].forEach((group) => {
    asArray(group).forEach((clinic) => {
      if (typeof clinic === 'string' || typeof clinic === 'number') {
        ids.push(clinic);
        return;
      }

      ids.push(
        clinic?.id,
        clinic?.clinic_id,
        clinic?.value,
        clinic?.clinic?.id,
        clinic?.clinic?.clinic_id,
        clinic?.pivot?.clinic_id,
        clinic?.doctor_clinic?.clinic_id
      );
    });
  });

  return unique(ids.map(text));
};

const collectClinicNames = (source = {}) => {
  const names = [
    source?.clinic_name,
    source?.clinicName,
    source?.clinic?.name,
    source?.clinic?.clinic_name,
    source?.assigned_clinic?.name,
    source?.assigned_clinic?.clinic_name,
    source?.clinic_admin?.clinic?.name,
    source?.clinic_admin?.clinic?.clinic_name,
    source?.doctor?.clinic_name,
    source?.patient?.clinic_name
  ];

  if (source?.clinic_type_id !== undefined || source?.pincode !== undefined || source?.type_name !== undefined) {
    names.push(source.name || source.clinic_name);
  }

  [
    source?.clinics,
    source?.assigned_clinics,
    source?.admin_clinics,
    source?.owned_clinics,
    source?.doctor_clinics,
    source?.doctor?.clinics,
    source?.patient?.clinics
  ].forEach((group) => {
    asArray(group).forEach((clinic) => {
      if (typeof clinic === 'string') {
        names.push(clinic);
        return;
      }

      names.push(clinic?.name, clinic?.clinic_name, clinic?.label, clinic?.clinic?.name, clinic?.clinic?.clinic_name);
    });
  });

  return unique(names.map((name) => text(name).toLowerCase()));
};

export const getClinicIds = collectClinicIds;
export const getClinicNames = collectClinicNames;

export const isAdminRoleValue = (role) => {
  const value = normalizeRoleValue(role);
  return value.includes('admin');
};

export const getCurrentAccess = (user = getStoredUser()) => {
  const role = getRoleFromUser(user);
  const clinicIds = collectClinicIds(user);
  const clinicNames = collectClinicNames(user);
  const hasClinicScope = clinicIds.length > 0 || clinicNames.length > 0;
  const explicitSuperAdmin = role.includes('super') && role.includes('admin');
  const explicitClinicAdmin = role.includes('admin') && (role.includes('clinic') || role.includes('clinical'));
  const plainAdmin = role === 'admin';
  const isDoctor = role.includes('doctor');
  const isClinicAdmin = !explicitSuperAdmin && (explicitClinicAdmin || (plainAdmin && hasClinicScope) || isDoctor);
  const isSuperAdmin = explicitSuperAdmin || (plainAdmin && !hasClinicScope);
  const isAdmin = isSuperAdmin || isClinicAdmin || role.includes('admin');
  const isLabAssistant = role.includes('lab_assistant') || role.includes('lab assistant');
  const isPharmacist = role.includes('pharmacist');
  const isPatient = !isAdmin && !isDoctor && !isLabAssistant && !isPharmacist;

  return {
    role,
    clinicIds,
    clinicNames,
    isAdmin,
    isSuperAdmin,
    isClinicAdmin,
    isDoctor,
    isLabAssistant,
    isPharmacist,
    isPatient,
    operationalRole: isDoctor ? 'doctor' : isAdmin ? 'admin' : isLabAssistant ? 'lab_assistant' : isPharmacist ? 'pharmacist' : role || 'patient'
  };
};

export const getLandingPathForUser = (user = getStoredUser()) => {
  const access = getCurrentAccess(user);

  if (access.isPharmacist) return '/dashboard/pharmacy';
  if (access.isDoctor) return '/dashboard/doctor';
  if (access.isLabAssistant) return '/dashboard/lab-assistant';
  if (access.isPatient) return '/dashboard/patient';

  return '/dashboard/default';
};

export const requiresClinicSetup = (user = getStoredUser()) => {
  const access = getCurrentAccess(user);
  if (!access.isClinicAdmin) return false;

  const setupComplete =
    user?.clinic_setup_completed ||
    user?.setup_completed ||
    user?.clinic?.setup_completed ||
    user?.clinic?.clinic_setup_completed ||
    localStorage.getItem('clinic_setup_completed') === 'true' ||
    sessionStorage.getItem('clinic_setup_completed') === 'true';

  if (setupComplete) return false;

  return Boolean(
    user?.first_login ||
      user?.is_first_login ||
      user?.must_change_password ||
      user?.clinic_setup_required ||
      user?.requires_setup ||
      user?.clinic?.requires_setup
  );
};

export const rowMatchesClinicScope = (row, access = getCurrentAccess()) => {
  if (!access?.isClinicAdmin) return true;

  const rowClinicIds = collectClinicIds(row);

  // Allow rows with no clinic assignment — unassigned users (e.g. patients)
  // should be visible to clinic admins so they can be linked to the clinic.
  if (rowClinicIds.length === 0) return true;

  if (access.clinicIds?.length && rowClinicIds.some((id) => access.clinicIds.includes(text(id)))) {
    return true;
  }

  const rowClinicNames = collectClinicNames(row);
  if (access.clinicNames?.length && rowClinicNames.some((name) => access.clinicNames.includes(text(name).toLowerCase()))) {
    return true;
  }

  return false;
};

export const withClinicScopeParams = (params = {}, access = getCurrentAccess()) => {
  if (!access?.isClinicAdmin) return params;

  const clinicId = access.clinicIds?.[0] || '';
  const clinicName = access.clinicNames?.[0] || '';

  return {
    ...params,
    scope: 'clinic',
    ...(clinicId ? { clinic_id: clinicId, clinic_ids: access.clinicIds.join(',') } : {}),
    ...(!clinicId && clinicName ? { clinic_name: clinicName } : {})
  };
};

export const adminRequestHeaders = (access = getCurrentAccess()) => {
  if (!access?.isAdmin) return {};

  const user = getStoredUser();
  const userId = user?.id || user?.user_id || localStorage.getItem('userId') || sessionStorage.getItem('userId') || '';
  const role = access.isSuperAdmin ? 'super_admin' : access.isClinicAdmin ? 'clinic_admin' : 'admin';
  const clinicId = access.clinicIds?.[0] || '';

  return {
    'X-Admin-Override': 'true',
    'X-Admin-Id': userId,
    'X-User-Id': userId,
    'X-User-Role': role,
    'X-Role': role,
    ...(clinicId ? { 'X-Clinic-Id': clinicId } : {})
  };
};

export const DEFAULT_ROLE_OPTIONS = [
  { id: null, value: 'super_admin', label: 'Super Admin' },
  { id: null, value: 'clinic_admin', label: 'Clinic Admin' },
  { id: null, value: 'doctor', label: 'Doctor' },
  { id: null, value: 'patient', label: 'Patient' },
  { id: null, value: 'pharmacist', label: 'Pharmacist' },
  { id: null, value: 'lab_assistant', label: 'Lab Assistant' },
  { id: null, value: 'receptionist', label: 'Receptionist' },
  { id: null, value: 'nurse', label: 'Nurse' },
  { id: null, value: 'accountant', label: 'Accountant' }
];

export const dedupeRoleOptions = (options) => { const seen = new Set(); return options.filter(opt => { if (seen.has(opt.value)) return false; seen.add(opt.value); return true; }); };
export const normalizeRoleOption = (role) => ({ value: normalizeRoleValue(role), label: roleLabel(role) });
